// MobilePinArt.tsx - Touch-optimized pin art canvas with haptic feedback
import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

interface Pin {
  x: number;
  y: number;
  z: number;      // Height displacement
  vz: number;     // Velocity in Z
  size: number;
  baseColor: string;
  holdTime: number; // Time remaining to hold displacement (write mode)
  imageZ: number;   // Target Z from image (photo mode)
}

type Mode = 'touch' | 'write' | 'photo' | 'zen';

interface TouchPoint {
  x: number;
  y: number;
  id: number;
}

interface MobilePinArtProps {
  width?: number;
  height?: number;
  gridSpacing?: number;
  enableHaptics?: boolean;
  primaryColor?: string;
  backgroundColor?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #0a0a0f;
  border-radius: 12px;
  overflow: hidden;
  touch-action: none; /* Prevent browser gestures */
  -webkit-user-select: none;
  user-select: none;
`;

const Canvas = styled.canvas`
  width: 100%;
  height: 100%;
  display: block;
  touch-action: none;
`;

const HintText = styled.div`
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  color: #444;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  pointer-events: none;
  text-align: center;
`;

const FPSCounter = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #0f0;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 4px;
`;

const ModeToggle = styled.button<{ $active: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${props => props.$active ? 'rgba(247, 147, 26, 0.3)' : 'rgba(0, 0, 0, 0.7)'};
  border: 2px solid ${props => props.$active ? '#f7931a' : '#333'};
  color: ${props => props.$active ? '#f7931a' : '#666'};
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:active {
    transform: scale(0.95);
  }
`;

const ModeButtons = styled.div`
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const SmallButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? 'rgba(247, 147, 26, 0.3)' : 'rgba(0, 0, 0, 0.7)'};
  border: 2px solid ${props => props.$active ? '#f7931a' : '#333'};
  color: ${props => props.$active ? '#f7931a' : '#666'};
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

// ============================================================================
// Haptic Feedback Utility
// ============================================================================

const triggerHaptic = (intensity: 'light' | 'medium' | 'heavy' = 'light') => {
  // Try Vibration API first (wider support)
  if ('vibrate' in navigator) {
    const durations: Record<string, number> = {
      light: 10,
      medium: 25,
      heavy: 50
    };
    navigator.vibrate(durations[intensity]);
  }
  
  // Try iOS-specific haptic feedback (requires user gesture context)
  // This works in PWAs and some Safari contexts
  try {
    if ((window as any).webkit?.messageHandlers?.haptic) {
      (window as any).webkit.messageHandlers.haptic.postMessage(intensity);
    }
  } catch (e) {
    // Silently fail - haptics not available
  }
};

// ============================================================================
// Physics Constants
// ============================================================================

const SPRING_STIFFNESS = 0.2;
const SPRING_DAMPING = 0.88;
const NEIGHBOR_INFLUENCE = 0.015; // Even smaller for hyper-fine grid
const TOUCH_RADIUS = 20;    // Smaller for finer control
const WAVE_SPEED = 60;      // Slower waves
const WAVE_WIDTH = 15;      // Finer waves
const WRITE_MODE_HOLD = 120; // 2 minutes - pins stay up very long
const WRITE_MODE_FADE = 0.008; // Super slow fade - takes ~2.5 mins to fully fade
const ULTRA_FINE_SPACING = 2; // Hyper-fine 2px spacing for maximum detail

// Zen mode pattern duration (seconds per pattern)
const ZEN_PATTERN_DURATION = 20; // Longer for relaxation
const ZEN_PATTERNS = ['breathing', 'mandala', 'ocean', 'circles', 'spiral'] as const;
const ZEN_DESCRIPTIONS: Record<typeof ZEN_PATTERNS[number], string> = {
  breathing: 'Breathe with the circle',
  mandala: 'Rotating flower mandala',
  ocean: 'Gentle ocean waves',
  circles: 'Expanding rings',
  spiral: 'Hypnotic spiral'
};

// ============================================================================
// Main Component
// ============================================================================

export const MobilePinArt: React.FC<MobilePinArtProps> = ({
  width,
  height,
  gridSpacing = ULTRA_FINE_SPACING,
  enableHaptics = true,
  primaryColor = '#f7931a',
  backgroundColor = '#0a0a0f'
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageCanvasRef = useRef<HTMLCanvasElement | null>(null);
  
  const [dimensions, setDimensions] = useState({ width: 400, height: 600 });
  const [fps, setFps] = useState(0);
  const [mode, setMode] = useState<Mode>('touch');
  const modeRef = useRef<Mode>('touch');
  const pulseTimeRef = useRef(0);
  const zenTimeRef = useRef(0);
  const zenPatternRef = useRef(0);
  
  // State refs for animation loop
  const pinsRef = useRef<Pin[]>([]);
  const touchesRef = useRef<TouchPoint[]>([]);
  const wavesRef = useRef<Array<{ time: number, x: number, y: number }>>([]);
  const lastHapticRef = useRef(0);
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameRef = useRef(performance.now());
  const gridInfoRef = useRef({ cols: 0, rows: 0 });
  
  // ============================================================================
  // Initialize Pin Grid
  // ============================================================================
  
  useEffect(() => {
    if (dimensions.width === 0) return;
    
    const pins: Pin[] = [];
    const padding = 15;
    const areaWidth = dimensions.width - padding * 2;
    const areaHeight = dimensions.height - padding * 2 - 60; // Leave room for hint
    
    const cols = Math.floor(areaWidth / gridSpacing);
    const rows = Math.floor(areaHeight / gridSpacing);
    
    const spacingX = areaWidth / Math.max(1, cols - 1);
    const spacingY = areaHeight / Math.max(1, rows - 1);
    
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = padding + col * spacingX;
        const y = padding + row * spacingY;
        
        // Subtle random darkness variation
        const rand = Math.random();
        const baseColor = rand > 0.7 ? '#1a1a1a' : rand > 0.4 ? '#141414' : '#0f0f0f';
        
        pins.push({
          x, y,
          z: 0,
          vz: 0,
          size: 0.8, // Tiny for hyper-fine 2px grid
          baseColor,
          holdTime: 0,
          imageZ: 0
        });
      }
    }
    
    pinsRef.current = pins;
    gridInfoRef.current = { cols, rows };
  }, [dimensions, gridSpacing]);
  
  // ============================================================================
  // Touch Handlers
  // ============================================================================
  
  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Trigger haptic on first touch
    if (enableHaptics && e.touches.length === 1) {
      triggerHaptic('light');
    }
    
    // Add a new wave from first touch point (only in touch mode)
    if (touches.length > 0 && modeRef.current === 'touch') {
      wavesRef.current.push({
        time: 0.001,
        x: touches[0].x,
        y: touches[0].y
      });
    }
    
    // In zen mode, touch changes to next pattern
    if (modeRef.current === 'zen') {
      zenPatternRef.current = (zenPatternRef.current + 1) % ZEN_PATTERNS.length;
      zenTimeRef.current = 0; // Reset time for smooth transition
    }
  }, [dimensions, enableHaptics]);
  
  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Throttled haptic feedback while moving
    if (enableHaptics) {
      const now = performance.now();
      if (now - lastHapticRef.current > 100) {
        // Check if any pin is highly displaced
        const maxZ = pinsRef.current.reduce((max, p) => Math.max(max, Math.abs(p.z)), 0);
        if (maxZ > 2) {
          triggerHaptic('light');
          lastHapticRef.current = now;
        }
      }
    }
  }, [dimensions, enableHaptics]);
  
  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const touches: TouchPoint[] = [];
    
    for (let i = 0; i < e.touches.length; i++) {
      const t = e.touches[i];
      touches.push({
        x: (t.clientX - rect.left) / rect.width * dimensions.width,
        y: (t.clientY - rect.top) / rect.height * dimensions.height,
        id: t.identifier
      });
    }
    
    touchesRef.current = touches;
    
    // Haptic on release
    if (enableHaptics && e.touches.length === 0) {
      triggerHaptic('medium');
    }
  }, [dimensions, enableHaptics]);
  
  // ============================================================================
  // Animation Loop
  // ============================================================================
  
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    
    const pins = pinsRef.current;
    const touches = touchesRef.current;
    const waves = wavesRef.current;
    const { cols, rows } = gridInfoRef.current;
    const { width: w, height: h } = dimensions;
    
    // Update all waves and remove expired ones
    for (let i = waves.length - 1; i >= 0; i--) {
      waves[i].time += 0.016;
      if (waves[i].time > 8) { // Longer lifetime for visual effect
        waves.splice(i, 1);
      }
    }
    
    const currentMode = modeRef.current;
    
    // Update pulse time for photo mode
    pulseTimeRef.current += 0.016;
    const pulsePhase = Math.sin(pulseTimeRef.current * 3) * 0.5 + 0.5; // 0-1 pulsing
    
    // Zen mode pattern time
    if (currentMode === 'zen') {
      zenTimeRef.current += 0.016;
      // Cycle through patterns
      if (zenTimeRef.current > ZEN_PATTERN_DURATION) {
        zenTimeRef.current = 0;
        zenPatternRef.current = (zenPatternRef.current + 1) % ZEN_PATTERNS.length;
      }
    }
    
    // Physics update
    for (let i = 0; i < pins.length; i++) {
      const p = pins[i];
      
      // Decay hold time
      if (p.holdTime > 0) {
        p.holdTime -= 0.016 * WRITE_MODE_FADE;
        if (p.holdTime < 0) p.holdTime = 0;
      }
      
      // Wave effects from ALL active waves (ONLY in touch mode)
      if (currentMode === 'touch') {
        for (const wave of waves) {
          const waveRadius = wave.time * WAVE_SPEED;
          const waveStrength = Math.max(0, 1 - wave.time / 8) * 8;
          
          const dx = p.x - wave.x;
          const dy = p.y - wave.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const distFromWave = Math.abs(dist - waveRadius);
          
          if (distFromWave < WAVE_WIDTH) {
            const phase = (distFromWave / WAVE_WIDTH) * Math.PI;
            // If pin is being held from write mode, wave just highlights (pushes less)
            const holdFactor = p.holdTime > 0 ? 0.3 : 1;
            p.vz += Math.sin(phase) * waveStrength * 0.12 * holdFactor;
          }
        }
      }
      
      // Photo mode: pins follow image silhouette with pulsation
      if (currentMode === 'photo' && p.imageZ > 0) {
        const targetZ = p.imageZ * (0.7 + pulsePhase * 0.6); // Pulsate between 70-130%
        p.vz += (targetZ - p.z) * 0.15;
      }
      
      // Zen mode: geometric patterns
      if (currentMode === 'zen') {
        const centerX = w / 2;
        const centerY = (h - 80) / 2; // Account for buttons at bottom
        const dx = p.x - centerX;
        const dy = p.y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);
        const t = zenTimeRef.current;
        const pattern = ZEN_PATTERNS[zenPatternRef.current];
        const maxRadius = Math.min(w, h) * 0.4;
        
        let targetZ = 0;
        
        switch (pattern) {
          case 'breathing': {
            // Single circle that expands and contracts like breathing
            // 4 second breath cycle: 2s in, 2s out
            const breathCycle = 4;
            const breathPhase = (t % breathCycle) / breathCycle;
            // Smooth sine wave for natural breathing feel
            const breathRadius = maxRadius * (0.3 + 0.7 * (Math.sin(breathPhase * Math.PI * 2 - Math.PI / 2) * 0.5 + 0.5));
            const ringWidth = 40;
            const distFromRing = Math.abs(dist - breathRadius);
            
            if (distFromRing < ringWidth) {
              // Smooth falloff from ring center
              const ringIntensity = 1 - (distFromRing / ringWidth);
              targetZ = ringIntensity * ringIntensity * 8; // Squared for softer edges
            }
            break;
          }
          case 'mandala': {
            // Rotating flower mandala with 6 petals
            const petals = 6;
            const rotationSpeed = 0.3;
            const rotatedAngle = angle + t * rotationSpeed;
            
            // Create petal shape
            const petalAngle = Math.abs(Math.sin(rotatedAngle * petals));
            const petalRadius = maxRadius * (0.4 + 0.5 * petalAngle);
            
            // Multiple layers
            const layer1 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.6) / 30);
            const layer2 = Math.max(0, 1 - Math.abs(dist - petalRadius * 0.8) / 25);
            const layer3 = Math.max(0, 1 - Math.abs(dist - petalRadius) / 20);
            
            // Pulsing center
            const centerPulse = Math.exp(-dist / 40) * (Math.sin(t * 2) * 0.3 + 0.7);
            
            targetZ = (layer1 * 4 + layer2 * 5 + layer3 * 6 + centerPulse * 7) * 0.5;
            break;
          }
          case 'ocean': {
            // Gentle horizontal waves like calm ocean
            const waveLength = 80;
            const waveSpeed = 1.5;
            const numWaves = 3;
            
            let totalWave = 0;
            for (let i = 0; i < numWaves; i++) {
              const offset = i * 0.7;
              const amp = 1 - i * 0.2; // Decreasing amplitude
              const freq = 1 + i * 0.3; // Slightly different frequencies
              totalWave += Math.sin((p.y / waveLength) * freq - t * waveSpeed + offset) * amp;
            }
            
            // Normalize and apply
            targetZ = (totalWave / numWaves) * 5 + 2; // Always slightly raised
            break;
          }
          case 'circles': {
            // Expanding rings one after another
            const ringInterval = 2; // Seconds between rings
            const ringSpeed = 80; // Pixels per second
            const ringWidth = 35;
            const numRings = 4;
            
            for (let i = 0; i < numRings; i++) {
              const ringAge = (t + i * ringInterval) % (ringInterval * numRings);
              const ringRadius = ringAge * ringSpeed;
              const distFromRing = Math.abs(dist - ringRadius);
              
              if (distFromRing < ringWidth && ringRadius < maxRadius * 1.5) {
                // Fade out as ring expands
                const fadeOut = Math.max(0, 1 - ringRadius / (maxRadius * 1.2));
                const ringIntensity = (1 - distFromRing / ringWidth) * fadeOut;
                targetZ = Math.max(targetZ, ringIntensity * 7);
              }
            }
            break;
          }
          case 'spiral': {
            // Slow mesmerizing spiral
            const spiralSpeed = 0.5;
            const spiralTightness = 0.015;
            const spiralWidth = 30;
            
            // Archimedean spiral: r = a + b*theta
            const spiralAngle = angle + t * spiralSpeed;
            const targetRadius = (spiralAngle + Math.PI) * spiralTightness * maxRadius / Math.PI;
            
            // Find how close this point is to any arm of the spiral
            let minDist = Infinity;
            for (let arm = -4; arm <= 4; arm++) {
              const armRadius = (spiralAngle + arm * Math.PI * 2 + Math.PI) * spiralTightness * maxRadius / Math.PI;
              if (armRadius > 0 && armRadius < maxRadius * 1.5) {
                minDist = Math.min(minDist, Math.abs(dist - armRadius));
              }
            }
            
            if (minDist < spiralWidth) {
              const intensity = 1 - minDist / spiralWidth;
              const fadeFromCenter = Math.min(1, dist / 50); // Fade near center
              targetZ = intensity * fadeFromCenter * 7;
            }
            break;
          }
        }
        
        // Smooth transition to target
        p.vz += (targetZ - p.z) * 0.1;
      }
      
      // Touch influence (touch and write modes)
      if (currentMode !== 'photo') {
        for (const touch of touches) {
          const dx = p.x - touch.x;
          const dy = p.y - touch.y;
          const distSq = dx * dx + dy * dy;
          const radiusSq = TOUCH_RADIUS * TOUCH_RADIUS;
          
          if (distSq < radiusSq) {
            const influence = 1 - distSq / radiusSq;
            
            if (currentMode === 'write') {
              // Write mode: set pin to raised state and hold it
              const targetZ = 6 * influence;
              if (targetZ > p.z) {
                p.z = targetZ;
                p.vz = 0;
              }
              p.holdTime = WRITE_MODE_HOLD;
            } else {
              // Touch mode: just push
              p.vz += influence * 1.2;
            }
          }
        }
      }
      
      // Spring physics - much slower return when holding or in photo/zen mode
      // When holdTime is active, pins barely move back at all
      const springMultiplier = (p.holdTime > 0) ? 0.005 : 
        (currentMode === 'photo' && p.imageZ > 0) ? 0.02 : 
        (currentMode === 'zen') ? 0.08 : 1;
      p.vz -= p.z * SPRING_STIFFNESS * springMultiplier;
      p.vz *= SPRING_DAMPING;
      p.z += p.vz;
      p.z = Math.max(-8, Math.min(12, p.z));
    }
    
    // Neighbor influence
    for (let row = 1; row < rows - 1; row++) {
      for (let col = 1; col < cols - 1; col++) {
        const idx = row * cols + col;
        if (idx >= pins.length) continue;
        
        const p = pins[idx];
        const top = pins[(row - 1) * cols + col];
        const bottom = pins[(row + 1) * cols + col];
        const left = pins[row * cols + (col - 1)];
        const right = pins[row * cols + (col + 1)];
        
        if (top && bottom && left && right) {
          const avg = (top.z + bottom.z + left.z + right.z) / 4;
          p.vz += (avg - p.z) * NEIGHBOR_INFLUENCE;
        }
      }
    }
    
    // Draw
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, w, h);
    
    // Group by color for batching
    const colorBuckets = new Map<string, Array<{x: number, y: number, size: number}>>();
    
    for (const p of pins) {
      const absZ = Math.abs(p.z);
      const displaySize = p.size + absZ * 0.4;
      const displayY = p.y - p.z * 0.8;
      
      let color: string;
      if (absZ > 2.5) {
        color = primaryColor;
      } else if (absZ > 1.2) {
        const intensity = (absZ - 1.2) / 1.3;
        const r = Math.round(50 + 197 * intensity);
        const g = Math.round(40 + 107 * intensity);
        const b = Math.round(20 + 6 * intensity);
        color = `rgb(${r},${g},${b})`;
      } else if (absZ > 0.4) {
        const intensity = (absZ - 0.4) / 0.8;
        const r = Math.round(25 + 25 * intensity);
        const g = Math.round(20 + 20 * intensity);
        const b = Math.round(15 * intensity);
        color = `rgb(${r},${g},${b})`;
      } else {
        color = '#1a1a1a';
      }
      
      if (!colorBuckets.has(color)) {
        colorBuckets.set(color, []);
      }
      colorBuckets.get(color)!.push({ x: p.x, y: displayY, size: displaySize });
    }
    
    // Draw batched
    colorBuckets.forEach((pinList, color) => {
      ctx.fillStyle = color;
      for (const pin of pinList) {
        ctx.fillRect(pin.x - pin.size, pin.y - pin.size, pin.size * 2, pin.size * 2);
      }
    });
    
    // Draw touch points (debug)
    ctx.fillStyle = 'rgba(247, 147, 26, 0.3)';
    for (const touch of touches) {
      ctx.beginPath();
      ctx.arc(touch.x, touch.y, TOUCH_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // FPS tracking
    const now = performance.now();
    frameTimesRef.current.push(now - lastFrameRef.current);
    lastFrameRef.current = now;
    if (frameTimesRef.current.length > 60) frameTimesRef.current.shift();
    
    if (frameTimesRef.current.length >= 30) {
      const avgFrame = frameTimesRef.current.reduce((a, b) => a + b) / frameTimesRef.current.length;
      setFps(Math.round(1000 / avgFrame));
    }
    
    animationRef.current = requestAnimationFrame(render);
  }, [dimensions, primaryColor, backgroundColor]);
  
  // ============================================================================
  // Effects
  // ============================================================================
  
  // Resize observer
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        setDimensions({
          width: width || clientWidth || 400,
          height: height || clientHeight || 600
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [width, height]);
  
  // Canvas setup
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimensions.width * dpr;
    canvas.height = dimensions.height * dpr;
    
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    
    // Start animation
    animationRef.current = requestAnimationFrame(render);
    
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [dimensions, render]);
  
  // Touch event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  // ============================================================================
  // Render
  // ============================================================================
  
  // Sync mode ref
  useEffect(() => {
    modeRef.current = mode;
    // Clear image data when leaving photo mode
    if (mode !== 'photo') {
      pinsRef.current.forEach(p => p.imageZ = 0);
    }
  }, [mode]);
  
  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    if (newMode === 'zen') {
      zenTimeRef.current = 0;
      zenPatternRef.current = 0;
    }
    if (enableHaptics) triggerHaptic('medium');
  }, [enableHaptics]);
  
  const handlePhotoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check if grid is initialized
    const { cols, rows } = gridInfoRef.current;
    if (cols === 0 || rows === 0) {
      console.warn('Grid not ready yet, cols:', cols, 'rows:', rows);
      alert('Please wait for the grid to load and try again');
      return;
    }
    
    console.log('Photo upload started, grid:', cols, 'x', rows, 'pins:', pinsRef.current.length);
    
    const img = new Image();
    // Don't set crossOrigin for blob URLs - it can cause issues
    
    img.onload = () => {
      console.log('Image loaded:', img.width, 'x', img.height);
      
      // Create offscreen canvas for image processing
      if (!imageCanvasRef.current) {
        imageCanvasRef.current = document.createElement('canvas');
      }
      const canvas = imageCanvasRef.current;
      canvas.width = cols;
      canvas.height = rows;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Could not get canvas context');
        return;
      }
      
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, cols, rows);
      
      // Draw image scaled to grid size
      const scale = Math.min(cols / img.width, rows / img.height);
      const drawW = img.width * scale;
      const drawH = img.height * scale;
      const offsetX = (cols - drawW) / 2;
      const offsetY = (rows - drawH) / 2;
      ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
      
      // Get image data and compute brightness for each pin
      const imageData = ctx.getImageData(0, 0, cols, rows);
      const pins = pinsRef.current;
      
      let affectedPins = 0;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const idx = row * cols + col;
          if (idx >= pins.length) continue;
          
          const pixelIdx = (row * cols + col) * 4;
          const r = imageData.data[pixelIdx];
          const g = imageData.data[pixelIdx + 1];
          const b = imageData.data[pixelIdx + 2];
          
          // Convert to grayscale brightness (0-1)
          const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
          
          // Use brightness directly - bright areas push pins up
          // More permissive threshold for better results
          const heightValue = brightness > 0.1 ? brightness * 8 : 0;
          
          pins[idx].imageZ = heightValue;
          if (heightValue > 0) affectedPins++;
        }
      }
      
      console.log('Photo processed, affected pins:', affectedPins, '/', pins.length);
      setMode('photo');
      if (enableHaptics) triggerHaptic('heavy');
    };
    
    img.onerror = (err) => {
      console.error('Image load error:', err);
      alert('Could not load image. Try a different image format (JPG/PNG).');
    };
    
    // Create blob URL and load
    const blobUrl = URL.createObjectURL(file);
    console.log('Loading image from blob:', blobUrl);
    img.src = blobUrl;
    
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [enableHaptics]);
  
  const getModeIcon = (m: Mode) => {
    switch (m) {
      case 'touch': return '👆';
      case 'write': return '✍️';
      case 'photo': return '📷';
      case 'zen': return '🧘';
    }
  };
  
  const getHintText = () => {
    switch (mode) {
      case 'touch': return 'Touch and swipe to interact';
      case 'write': return 'Draw to make pins stay raised';
      case 'photo': return 'Pins pulsate to show your image';
      case 'zen': return `${ZEN_DESCRIPTIONS[ZEN_PATTERNS[zenPatternRef.current]]} • Tap to change`;
    }
  };
  
  return (
    <Container ref={containerRef}>
      <Canvas ref={canvasRef} />
      <ModeButtons>
        <SmallButton $active={mode === 'touch'} onClick={() => handleModeChange('touch')}>
          👆 Touch
        </SmallButton>
        <SmallButton $active={mode === 'write'} onClick={() => handleModeChange('write')}>
          ✍️ Write
        </SmallButton>
        <SmallButton $active={mode === 'photo'} onClick={() => fileInputRef.current?.click()}>
          📷 Photo
        </SmallButton>
        <SmallButton $active={mode === 'zen'} onClick={() => handleModeChange('zen')}>
          🧘 Zen
        </SmallButton>
      </ModeButtons>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoUpload}
      />
      <HintText>{getHintText()}</HintText>
      <FPSCounter>{fps} FPS | {pinsRef.current.length} pins</FPSCounter>
    </Container>
  );
};

export default MobilePinArt;
